import { Notice } from '../../components/ui/Notice';
import type { CapabilityResult } from './settings-api';

/**
 * 🔴 WHETHER THIS SCREEN CAN WRITE — as a fact from the server, not an assumption.
 *
 * `GET /api/v1/settings` answers `{ collections, writable }`. That flag was `false` while the write
 * routes were unbuilt; it is `true` now that they exist.
 *
 * ⭐⭐ FOUR STATES, NOT TWO. Every one of them disables the same controls, and a reader needs to know
 * WHICH, because the four lead to four different next actions:
 *   · enabled     → save away
 *   · unbuilt     → the routes do not exist; somebody has to ship them
 *   · unknown     → we could not ask; retry, or check the deployment
 *   · admin-only  → they exist, they work, and this account may not use them; ask an admin
 * ⛔ Collapsing them into a boolean is how "the settings page is read-only" becomes folklore that
 * outlives the deployment that caused it.
 *
 * 🔴🔴 `writable: true` IS NOT A STATEMENT ABOUT *YOU*, AND READING IT AS ONE WAS THE PH-26 TRAP.
 *   The probe is `requireUser`; every WRITE is `[requireUser, requireAdmin]` (API `routes/settings.ts`
 *   — `const adminOnly = { preHandler: [requireUser, requireAdmin] }` on POST, PATCH, PUT and
 *   DELETE). So the flag answers "does this BUILD have the routes", never "may this ACCOUNT call
 *   them", and a screen that took it as permission would enable every control for every user and
 *   403 on every save. The admin check therefore happens HERE as well, and it is checked FIRST.
 */
export type WriteGate =
  | { kind: 'enabled' }
  /** The API advertises `writable: false` — the routes exist in the README and not in the build. */
  | { kind: 'unbuilt' }
  /** The probe itself is missing or failed. We do not know, and we say we do not know. */
  | { kind: 'unknown'; why: string }
  /**
   * The routes exist and this account is not an administrator.
   * ⛔ NOT the access control — the API's `requireAdmin` is, independently, and would 403 this same
   * user if they reached the endpoint with curl. This exists so the screen does not invite a click
   * whose only possible outcome is a refusal.
   */
  | { kind: 'admin-only' };

export function gateFrom(
  result: CapabilityResult | null,
  probeFailed: string | null,
  /**
   * `is_admin` off the SESSION (`/auth/me`) — the server's answer, not one this app decided.
   * ⚠️ It is checked BEFORE the capability probe on purpose. A non-admin's controls must be disabled
   * even while the probe is still in flight, otherwise there is a window in which every button is
   * live for somebody who cannot use one.
   */
  isAdmin: boolean,
): WriteGate {
  if (!isAdmin) return { kind: 'admin-only' };
  if (probeFailed) return { kind: 'unknown', why: probeFailed };
  if (!result) return { kind: 'unknown', why: 'The capability probe has not answered yet.' };
  if (result.kind === 'endpoint-missing') {
    return {
      kind: 'unknown',
      why: `${result.path} is not deployed, so this build cannot say whether settings are writable.`,
    };
  }
  return result.capability.writable ? { kind: 'enabled' } : { kind: 'unbuilt' };
}

export function canWrite(gate: WriteGate): boolean {
  return gate.kind === 'enabled';
}

/**
 * ⛔ The disabled controls stay ON SCREEN and disabled rather than being hidden.
 *
 * A hidden button teaches nobody anything: the next reader concludes the feature was never
 * specified, re-derives the whole finding, and files it again. A disabled one with this notice above
 * it says exactly which endpoint has to land.
 */
export function WriteGateNotice({ gate }: { gate: WriteGate }) {
  if (gate.kind === 'enabled') return null;

  /**
   * ⭐ THE `/admin` PRECEDENT, APPLIED TO A SCREEN THAT IS HALF-OPEN. `/admin` refuses the whole
   * page with an explanation rather than a redirect; here the READS are genuinely open to everyone
   * (the GETs are `requireUser`), so refusing the page would hide data this user is entitled to.
   * The write half is what is refused, and it says so in the same voice.
   */
  if (gate.kind === 'admin-only') {
    return (
      <Notice tone="info" title="Read-only — these are global settings and only an admin may change them">
        <p>
          Statuses, types, priorities, impacts, resolutions and the transition matrix are{' '}
          <strong>one set for the whole installation</strong>, not per project and not per person —
          renaming a status here renames it on every ticket in every project. The API guards each
          write with <code>requireAdmin</code>, so a save from this account would come back{' '}
          <code>403</code>.
        </p>
        <p className="mt-1">
          ⭐ Everything below is real, current data and is safe to read. The controls are disabled
          rather than hidden, so what exists and who may change it are both visible. Ask an
          administrator, or have the flag granted to you on <code>/admin</code>.
        </p>
      </Notice>
    );
  }

  if (gate.kind === 'unknown') {
    return (
      <Notice tone="warning" title="Cannot tell whether settings can be edited">
        <p>{gate.why}</p>
        <p className="mt-1">
          Every control below is disabled — ⛔ not because editing is forbidden, but because enabling
          a button we cannot back up would produce a save that fails in a way nobody could explain.
        </p>
      </Notice>
    );
  }

  return (
    <Notice tone="warning" title="This PlanHub API is read-only for settings">
      <p>
        <code>GET /api/v1/settings</code> answers <code>writable: false</code>. That is the API's own
        advertisement of a gap, not a permission error — the write routes are specified and{' '}
        <strong>not built</strong>.
      </p>
      <p className="mt-1">Editing here needs these to land:</p>
      <ul className="mt-1 ml-4 list-disc font-mono text-xs">
        <li>POST /api/v1/settings/{'{collection}'}</li>
        <li>PATCH /api/v1/settings/{'{collection}'}/{'{code}'}</li>
        <li>PUT /api/v1/settings/transitions</li>
        <li>GET · POST · PATCH /api/v1/projects/{'{id}'}/modules</li>
      </ul>
      {/*
        ⚠️ A second gap used to be listed here — `PUT` missing from the API's CORS allow-list, which
        would have blocked the matrix save at the preflight even once the route existed. It was
        reported and FIXED; `app.ts` now allows PUT and its test pins the whole method set. The line
        is removed rather than kept as history: a notice that names a problem which no longer exists
        sends the reader to check something that is fine.
      */}
      <p className="mt-1">
        ⭐ Everything below is real, current data and is safe to read. The controls are disabled
        rather than hidden so that what is missing is visible.
      </p>
    </Notice>
  );
}

/** The reason a control is disabled, as a `title` — so a hover answers "why can't I click this?". */
export function disabledReason(gate: WriteGate): string | undefined {
  if (gate.kind === 'enabled') return undefined;
  if (gate.kind === 'unknown') return `Writes unavailable — ${gate.why}`;
  if (gate.kind === 'admin-only') {
    return 'Read-only — these settings are global and the API allows only an administrator to change them.';
  }
  return 'Writes unavailable — the PlanHub API advertises writable: false for settings.';
}
